/**
 * Stretch Engine Types
 * Turn-based dungeon exploration system — 10-minute in-game turns.
 * Pure data types, no functions or callbacks.
 */

import type { GridMap, FogState, TriggerEvent } from '@omen-quest/grid-map';

// ============================================================================
// PHASE MACHINE
// ============================================================================

export type StretchPhase = 'planning' | 'resolving' | 'events' | 'ended';

export type StretchOutcome =
  | 'active'
  | 'completed'
  | 'fled'
  | 'combat_triggered';

// ============================================================================
// CRAWL STRATEGY
// ============================================================================

export type LightSourceType = 'torch' | 'lantern' | 'magic' | 'other';

export interface LightSource {
  id: string;
  carrierId: string;
  type: LightSourceType;
  remainingStretches: number;
  radius: number;
}

export interface CrawlStrategy {
  lightSources: LightSource[];
  stealthMode: boolean;
}

// ============================================================================
// PLAYERS
// ============================================================================

export interface StretchPlayer {
  id: string;
  name: string;
  x: number;
  y: number;
  movementBudget: number;
  visionRadius: number;
  passivePerception: number;
  fogGrid: FogState[][];
  lightGrid: number[][];
  hasCommitted: boolean;
  isKeepingWatch: boolean;
  isStealthed: boolean;
}

// ============================================================================
// ACTIVITIES
// ============================================================================

export type StretchActivity =
  | { type: 'move'; path: Array<{ x: number; y: number }> }
  | { type: 'search' }
  | { type: 'explore'; path?: Array<{ x: number; y: number }> }
  | { type: 'keep_watch' }
  | { type: 'move_quietly'; path?: Array<{ x: number; y: number }> }
  | { type: 'interact'; x: number; y: number; description?: string }
  | { type: 'help'; targetPlayerId: string }
  | { type: 'loot' }
  | { type: 'cast_spell'; spellName?: string }
  | { type: 'take_your_time'; description: string }
  | { type: 'wait' };

// ============================================================================
// INPUTS
// ============================================================================

export type StretchInput =
  | { type: 'commit'; playerId: string; activity: StretchActivity }
  | { type: 'step' };

// ============================================================================
// COMMITS
// ============================================================================

export interface StretchCommit {
  playerId: string;
  activity: StretchActivity;
  helpBonusFrom?: string;
}

// ============================================================================
// WANDERING MONSTERS
// ============================================================================

export type WanderingFrequency = 'constant' | 'medium' | 'hourly';
export type WanderingLikelihood = 'unlikely' | 'possible' | 'inevitable';

export interface EncounterTableEntry {
  roll: [number, number];
  encounterId: string;
  description: string;
  difficulty: 'trivial' | 'challenging' | 'deadly';
}

export interface WanderingMonsterConfig {
  frequency: WanderingFrequency;
  likelihood: WanderingLikelihood;
  encounterTable: EncounterTableEntry[];
  stretchesSinceLastCheck: number;
}

/** Frequency → number of stretches between checks */
export const FREQUENCY_INTERVALS: Record<WanderingFrequency, number> = {
  constant: 1,
  medium: 3,
  hourly: 6,
};

/** Likelihood → threshold on a d6 (roll <= threshold triggers encounter) */
export const LIKELIHOOD_THRESHOLDS: Record<WanderingLikelihood, number> = {
  unlikely: 1,
  possible: 2,
  inevitable: 3,
};

// ============================================================================
// TIMELINE & COUNTDOWNS
// ============================================================================

export type TimelineEventType =
  | 'environment'
  | 'discovery'
  | 'narrative'
  | 'reinforcement'
  | 'countdown_zero';

export interface EventCondition {
  type: 'flag_set' | 'flag_not_set' | 'player_in_area';
  flag?: string;
  area?: Array<{ x: number; y: number }>;
}

export interface TimelineEvent {
  id: string;
  triggerStretch: number;
  type: TimelineEventType;
  data: Record<string, unknown>;
  condition?: EventCondition;
  fired?: boolean;
}

export interface Countdown {
  id: string;
  label: string;
  remainingStretches: number;
  effect: TimelineEvent;
  visible: boolean;
}

// ============================================================================
// RESOLUTION
// ============================================================================

export interface ActivityResult {
  playerId: string;
  activityType: StretchActivity['type'];
  tilesRevealed: number;
  itemsFound: string[];
  triggerEvents: TriggerEvent[];
  notes: string[];
}

export interface WanderingMonsterResult {
  checked: boolean;
  rolled?: number;
  threshold?: number;
  triggered: boolean;
  encounter?: EncounterTableEntry;
}

export interface StretchResolution {
  stretchNumber: number;
  activityResults: ActivityResult[];
  timelineEventsFired: TimelineEvent[];
  wanderingMonster: WanderingMonsterResult;
  countdownsExpired: Countdown[];
  lightSourcesDepleted: LightSource[];
}

// ============================================================================
// CORE STATE
// ============================================================================

export interface StretchState {
  phase: StretchPhase;
  stretchNumber: number;

  map: GridMap;
  openDoors: Set<string>;
  firedTriggers: Set<string>;
  triggerFlags: Set<string>;

  crawlStrategy: CrawlStrategy;

  players: StretchPlayer[];
  commits: StretchCommit[];

  wanderingMonsters: WanderingMonsterConfig;

  timeline: TimelineEvent[];

  countdowns: Countdown[];

  lastResolution: StretchResolution | null;

  outcome: StretchOutcome;
}

// ============================================================================
// CONFIG (for createStretchState)
// ============================================================================

export interface StretchConfig {
  map: GridMap;
  players: Array<{
    id: string;
    name: string;
    x: number;
    y: number;
    movementBudget?: number;
    visionRadius?: number;
    passivePerception?: number;
  }>;
  crawlStrategy?: Partial<CrawlStrategy>;
  wanderingMonsters?: Partial<WanderingMonsterConfig>;
  timeline?: TimelineEvent[];
  countdowns?: Countdown[];
  openDoors?: Set<string>;
  firedTriggers?: Set<string>;
  triggerFlags?: Set<string>;
}

// ============================================================================
// DEFAULTS
// ============================================================================

export const DEFAULT_MOVEMENT_BUDGET = 6;
export const DEFAULT_VISION_RADIUS = 0;
export const DEFAULT_PASSIVE_PERCEPTION = 10;
export const SEARCH_RADIUS = 8;
export const EXPLORE_VISION_MULTIPLIER = 1.5;
export const STEALTH_SPEED_MULTIPLIER = 0.5;
